import { CartAddEvent } from '@theme/events';

/**
 * A custom element that handles adding products to cart and opening the cart drawer.
 * 
 * @extends {HTMLElement}
 */
class AddToCardComponent extends HTMLElement {
  connectedCallback() {
    this.#button = this.querySelector('.add-to-card-button');
    this.#productId = this.dataset.productId || null;
    
    if (this.#button) {
      this.#button.addEventListener('click', this.#handleAddToCart);
    }
  }

  disconnectedCallback() {
    if (this.#button) {
      this.#button.removeEventListener('click', this.#handleAddToCart);
    }
  }

  #button = null;
  #productId = null;

  #handleAddToCart = async (event) => {
    event.preventDefault();
    
    if (!this.#productId) {
      console.error('Add to card: Product ID not found');
      return;
    }

    // Get variant ID from various sources
    const variantId = this.#getSelectedVariantId();
    
    if (!variantId) {
      console.error('Add to card: No variant ID found');
      return;
    }

    // Prepare form data
    const formData = new FormData();
    formData.append('id', variantId);
    formData.append('quantity', '1');
    
    // Show loading state
    if (this.#button) {
      this.#button.classList.add('loading');
      this.#button.disabled = true;
    }

    try {
      // Add to cart via fetch
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        body: formData,
        headers: {
          'Accept': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();

      // Dispatch cart add event
      document.dispatchEvent(
        new CartAddEvent({}, this.#productId, {
          source: 'add-to-card-component',
          itemCount: result.quantity || 1,
          productId: this.#productId,
          variantId: result.variant_id,
        })
      );

      // Open cart drawer automatically
      this.#openCartDrawer();

      // Show success feedback for a moment
      setTimeout(() => {
        if (this.#button) {
          this.#button.classList.remove('loading');
          this.#button.disabled = false;
        }
      }, 1500);

    } catch (error) {
      console.error('Error adding to cart:', error);
      
      // Remove loading state on error
      if (this.#button) {
        this.#button.classList.remove('loading');
        this.#button.disabled = false;
      }
      
      // Show error feedback
      this.#showError();
    }
  };

  /**
   * Get the selected variant ID from various sources
   * @returns {string|null} The variant ID
   */
  #getSelectedVariantId = () => {
    // 0. First try to get from our own data attribute (most reliable)
    if (this.dataset.variantId) {
      console.log('Add to card: Using variant ID from data attribute:', this.dataset.variantId);
      return this.dataset.variantId;
    }

    // 1. Try to get from product form component (if exists)
    const productForm = document.querySelector('product-form-component');
    if (productForm) {
      const variantInput = productForm.querySelector('[name="id"]');
      if (variantInput && variantInput.value) {
        return variantInput.value;
      }
    }

    // 2. Try to get from product card variant picker
    const productCard = this.closest('product-card');
    if (productCard) {
      const variantPicker = productCard.querySelector('variant-picker');
      if (variantPicker) {
        const selectedOption = variantPicker.querySelector('[checked], [selected]');
        if (selectedOption && selectedOption.dataset.variantId) {
          return selectedOption.dataset.variantId;
        }
      }
    }

    // 3. Try to get from swatches
    const swatchesComponent = this.closest('.product-card, product-card')?.querySelector('product-swatches');
    if (swatchesComponent) {
      const selectedSwatch = swatchesComponent.querySelector('[data-variant-id][aria-current="true"]');
      if (selectedSwatch && selectedSwatch.dataset.variantId) {
        return selectedSwatch.dataset.variantId;
      }
    }

    // 4. Try to get from URL parameter (for product pages)
    const urlParams = new URLSearchParams(window.location.search);
    const variantFromUrl = urlParams.get('variant');
    if (variantFromUrl) {
      return variantFromUrl;
    }

    // 5. Fallback: get first available variant from product data
    // This assumes the product data is available somewhere in the DOM
    const productDataScript = document.querySelector(`script[data-product-id="${this.#productId}"]`);
    if (productDataScript) {
      try {
        const productData = JSON.parse(productDataScript.textContent);
        if (productData.variants && productData.variants.length > 0) {
          // Find first available variant
          const availableVariant = productData.variants.find(v => v.available);
          if (availableVariant) {
            return availableVariant.id.toString();
          }
          // If no available variant, use the first one
          return productData.variants[0].id.toString();
        }
      } catch (e) {
        console.warn('Could not parse product data from script tag');
      }
    }

    // 6. Last resort: try to find any variant ID in the DOM
    const anyVariantElement = document.querySelector(`[data-variant-id], [data-product-variant-id]`);
    if (anyVariantElement) {
      return anyVariantElement.dataset.variantId || anyVariantElement.dataset.productVariantId;
    }

    return null;
  };

  #openCartDrawer = () => {
    const cartDrawer = document.querySelector('cart-drawer-component');
    if (cartDrawer && typeof cartDrawer.open === 'function') {
      cartDrawer.open();
    } else {
      // Fallback: try to open any cart drawer element
      const drawerTrigger = document.querySelector('[data-cart-drawer-trigger]') || 
                           document.querySelector('.cart-icon');
      if (drawerTrigger) {
        drawerTrigger.click();
      }
    }
  };

  #showError = () => {
    // Add error class for visual feedback
    if (this.#button) {
      this.#button.classList.add('error');
      
      // Remove error class after a moment
      setTimeout(() => {
        if (this.#button) {
          this.#button.classList.remove('error');
        }
      }, 3000);
    }
  };
}

// Define the custom element if not already defined
if (!customElements.get('add-to-card-component')) {
  customElements.define('add-to-card-component', AddToCardComponent);
}